// Reusable Button — single style source so every feature module renders
// CTAs that look and behave the same way (focus ring, disabled state,
// dark-mode contrast). Variants are intentionally limited to "primary"
// and "secondary" to keep the design system small; extend the union type
// when a new visual treatment is genuinely needed.

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant;
    size?: Size;
}

const VARIANT_CLASSES: Record<Variant, string> = {
    primary:
        'bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 ' +
        'hover:shadow-green-500/30 hover:-translate-y-0.5 shadow-xl',
    secondary:
        'bg-transparent border-2 border-slate-300 dark:border-slate-700 ' +
        'text-slate-900 dark:text-white hover:bg-slate-300 dark:hover:bg-slate-800',
    ghost:
        'bg-transparent text-slate-900 dark:text-white ' +
        'hover:bg-slate-200/60 dark:hover:bg-slate-800/60',
};

const SIZE_CLASSES: Record<Size, string> = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = 'primary', size = 'md', className = '', type = 'button', ...rest }, ref) => {
        const classes = [
            'inline-flex items-center justify-center rounded-xl font-bold',
            'transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0',
            VARIANT_CLASSES[variant],
            SIZE_CLASSES[size],
            className,
        ].join(' ');

        return <button ref={ref} type={type} className={classes} {...rest} />;
    },
);
Button.displayName = 'Button';

export default Button;
